import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { DateInputComponent } from '../../../../../shared/components/date-input/date-input.component';
@Component({
  selector: 'app-zoho-statement-download-dialog',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatDialogModule, MatButtonModule, DateInputComponent],
  template: `
    <h2
      mat-dialog-title
      class="dialog-title"
    >
      Download Statement
    </h2>

    <mat-dialog-content>
      <p class="subtitle">Select a date range to get statement.</p>

      <form
        [formGroup]="form"
        class="dialog-form"
      >
        <div class="date-container">
          <app-date-input
            label="Start Date"
            [control]="getControl('startDate')"
            [max]="form.get('endDate')?.value || today"
          >
            <span
              error
              *ngIf="form.get('startDate')?.hasError('required')"
            >
              Start date is required
            </span>
          </app-date-input>

          <app-date-input
            label="End Date"
            [control]="getControl('endDate')"
            [min]="form.get('startDate')?.value"
            [max]="today"
          >
            <span
              error
              *ngIf="form.get('endDate')?.hasError('required')"
            >
              End date is required
            </span>
          </app-date-input>
        </div>

        <div
          class="error"
          *ngIf="form.errors?.['dateRangeInvalid']"
        >
          Start date cannot be after end date
        </div>
      </form>
    </mat-dialog-content>

    <mat-dialog-actions class="actions">
      <button
        mat-button
        (click)="onCancel()"
      >
        Cancel
      </button>

      <button
        mat-flat-button
        color="primary"
        [disabled]="form.invalid"
        (click)="onDownload()"
      >
        Download
      </button>
    </mat-dialog-actions>
  `,
  styles: [
    `
      .dialog-title {
        font-weight: 600;
        font-size: 20px;
      }

      .subtitle {
        margin: 4px 0 16px;
        color: #6b7280;
        font-size: 14px;
      }

      .dialog-form {
        display: flex;
        flex-direction: column;
        gap: 20px;
      }

      .date-container {
        display: flex;
        flex-direction: column;
        gap: 8px;
      }

      app-date-input {
        width: 100%;
      }

      .actions {
        display: flex;
        justify-content: flex-end;
        gap: 12px;
        margin-top: 16px;
      }

      button[mat-flat-button] {
        padding: 8px 20px;
        border-radius: 8px;
        font-weight: 500;
      }

      .error {
        color: #dc2626;
        font-size: 13px;
        margin-top: -10px;
      }

      @media (max-width: 600px) {
        .dialog-title {
          font-size: 18px;
        }

        .subtitle {
          font-size: 13px;
        }

        .actions {
          flex-direction: column-reverse;
          align-items: stretch;
        }

        .actions button {
          width: 100%;
        }

        button[mat-flat-button] {
          padding: 10px;
        }
      }
    `,
  ],
})
export class ZohoStatementDownloadDialogComponent {
  form: FormGroup;
  today = new Date();

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<ZohoStatementDownloadDialogComponent>
  ) {
    this.form = this.fb.group(
      {
        startDate: [null, Validators.required],
        endDate: [null, Validators.required],
      },
      { validators: this.dateRangeValidator }
    );
  }

  getControl(name: string): FormControl {
    return this.form.get(name) as FormControl;
  }

  dateRangeValidator(control: AbstractControl): ValidationErrors | null {
    const start = control.get('startDate')?.value;
    const end = control.get('endDate')?.value;

    if (start && end && start > end) {
      return { dateRangeInvalid: true };
    }

    return null;
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onDownload(): void {
    if (this.form.valid) {
      this.dialogRef.close(this.form.value);
    }
  }
}
